import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './PaymentPage.css';
import { getMetaTracking } from './analytics/metaTracking';
import { dlPushContent } from './analytics/analytics';
import { WA_CS_LINK } from './config/whatsapp';
import useFreshIdToken from './auth/useFreshIdToken';
import { useLanguage } from './i18n/LanguageContext';
import RichText from './i18n/RichText';

// Subtitles live in payment.methods.<id>.
const PAYMENT_METHODS = [
    {
        id: 'qris',
        name: 'QRIS',
        icon: 'https://d1qsbuaxtzmcn4.cloudfront.net/payment/qris-removebg-preview.png',
    },
    {
        id: 'gopay',
        name: 'Gopay / Gopay Tabungan',
        icon: 'https://d1qsbuaxtzmcn4.cloudfront.net/payment/gopay.webp',
    },
];

const PaymentPage = () => {
    const { productCode } = useParams();
    const {
        user,
        isAuthenticated,
        isLoading,
        loginWithRedirect,
    } = useAuth0();
    const getFreshIdToken = useFreshIdToken();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const query = new URLSearchParams(search);
    const topup = query.get('topup') === '1';
    const iccid = query.get('iccid') || undefined;
    const SimId = query.get('SimId') || undefined;

    // Tracking/passthrough query for the backend, without our internal top-up keys.
    const apiSearch = (() => {
        const params = new URLSearchParams(search);
        params.delete('topup');
        params.delete('iccid');
        params.delete('SimId');
        const qs = params.toString();
        return qs ? `?${qs}` : '';
    })();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [orderLoading, setOrderLoading] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoError, setPromoError] = useState('');
    const [promoData, setPromoData] = useState(null);

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            localStorage.setItem("redirectAfterLogin", `${pathname}${search}`);
            loginWithRedirect({ appState: { returnTo: `${pathname}${search}` } })
                .catch(err => console.error('Login error:', err));
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, pathname, search]);

    useEffect(() => {
        fetch(`https://api.artamobile.id/v1/product?productCode=${productCode}`)
            .then(res => { if (!res.ok) throw new Error('Failed'); return res.json(); })
            .then(data => { setProduct(data); setLoading(false); })
            .catch(err => { setError(err); setLoading(false); });
    }, [productCode]);

    useEffect(() => {
        if (!product || loading || error) return;
        dlPushContent({
            event: "view_content",
            content_type: "product",
            content_ids: [product.code || productCode],
            value: Number(product.price) || 0,
            currency: "IDR",
            product_code: product.code || productCode,
            region_name: product.regionName || "",
            balance: product.balance || "",
            validity_days: product.validity || "",
            topup: !!topup,
        });
    }, [product, loading, error, productCode, topup]);

    const handleApplyPromo = async (event) => {
        event.preventDefault();
        const trimmedCode = promoCode.trim();
        if (!trimmedCode) { setPromoError(t('payment.promoEmpty')); setPromoData(null); return; }
        setPromoLoading(true);
        setPromoError('');

        // The promo route is authenticated: it reads the customer's email from
        // this token to apply per-user promo rules.
        let token;
        try {
            token = await getFreshIdToken();
        } catch (e) {
            setPromoData(null);
            setPromoError(e.message);
            setPromoLoading(false);
            return;
        }
        if (!token) {
            setPromoData(null);
            setPromoError(t('common.sessionExpired'));
            setPromoLoading(false);
            return;
        }

        fetch(`https://api.artamobile.id/v2/promo/validate/${encodeURIComponent(productCode)}/${encodeURIComponent(trimmedCode)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => {
                if (res.status === 401 || res.status === 403) throw new Error('AUTH');
                if (!res.ok) throw new Error('Failed');
                return res.json();
            })
            .then(data => {
                if (data?.success) { setPromoData(data); }
                else { setPromoData(null); setPromoError(t('payment.promoNotFound')); }
            })
            .catch((e) => {
                setPromoData(null);
                setPromoError(e.message === 'AUTH'
                    ? t('common.sessionExpired')
                    : t('payment.promoInvalid'));
            })
            .finally(() => setPromoLoading(false));
    };

    const handlePay = () => {
        if (!selectedMethod) return;
        dlPushContent({
            event: "initiate_checkout",
            content_type: "product",
            content_ids: [product?.code || productCode],
            // What the customer will actually pay, after any promo.
            value: Number(effectivePrice) || 0,
            currency: "IDR",
            payment_method: selectedMethod,
            product_code: product?.code || productCode,
            region_name: product?.regionName || "",
            topup: !!topup,
        });
        setOrderLoading(true);
        const payload = {
            productCode,
            paymentType: selectedMethod,
            email: user.email,
            customerName: user.name,
            meta_tracking: getMetaTracking(),
        };
        // The redemption row already holds the promo code and the priced-out
        // discount, so the order references it by id rather than re-sending the
        // code for the backend to revalidate.
        if (promoData?.success && promoData.redemptionId) payload.redemptionId = promoData.redemptionId;
        let url;
        if (topup) {
            payload.iccid = iccid;
            payload.SimId = SimId;
            url = `https://api.artamobile.id/v1/topup${apiSearch}`;
        } else {
            payload.customerPhone = "";
            url = `https://api.artamobile.id/v1/order${apiSearch}`;
        }
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
            .then(res => { if (!res.ok) throw new Error('Failed'); return res.json(); })
            .then(data => {
                setOrderLoading(false);
                if (data?.method === 'qr' && data.qrString) {
                    navigate('/qris', { state: { qrString: data.qrString, expiryTime: data.expiryTime, orderId: data.orderId, amount: effectivePrice } });
                } else if (data?.method === 'redirect') {
                    window.location.href = data.deeplinkRedirect;
                } else {
                    console.error('Unexpected payment response:', data);
                }
            })
            .catch(err => { console.error('Payment error:', err); setOrderLoading(false); });
    };

    if (loading || orderLoading) {
        return <div className="loader-wrapper"><div className="loader"></div></div>;
    }

    const fmt = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;
    const effectivePrice = promoData?.success ? promoData.finalPrice : product?.price;
    const originalDisplayPrice = promoData?.success ? product?.price : product?.originalPrice;
    const discountPct = originalDisplayPrice && effectivePrice
        ? Math.round((1 - effectivePrice / originalDisplayPrice) * 100)
        : null;

    const cardTitle = topup
        ? t('payment.topupTitle', { region: product?.regionName || '' })
        : `${product?.regionName || ''}${product?.balance ? ' ' + product.balance : ''}`;

    return (
        <div className="pp-page">

            {/* ── TopAppBar ── */}
            <div className="pp-appbar">
                <button className="pp-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <img src={`${process.env.PUBLIC_URL}/icons/arrow-left.svg`} width={16} height={16} alt="" />
                </button>
                <h1 className="pp-appbar-title">{t('payment.title')}</h1>
            </div>

            <div className="pp-scroll">
                <div className="pp-content">

                    {/* ── Package card + email bar group ── */}
                    <div className="pp-pkg-group">
                    <div
                        className="pp-pkg-card"
                        style={product?.regionImage ? {
                            backgroundImage: `linear-gradient(to top, #ffffff 45%, rgba(255,255,255,0.55) 100%), url(${product.regionImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        } : {}}
                    >
                        {/* Decorative QR pattern at top-right */}
                        <div className="pp-pkg-qr" aria-hidden="true">
                            <svg viewBox="0 0 136 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0" y="0" width="45" height="45" rx="4" fill="none" stroke="#434343" strokeWidth="3" opacity="0.15"/>
                                <rect x="7" y="7" width="31" height="31" rx="2" fill="#434343" opacity="0.1"/>
                                <rect x="91" y="0" width="45" height="45" rx="4" fill="none" stroke="#434343" strokeWidth="3" opacity="0.15"/>
                                <rect x="98" y="7" width="31" height="31" rx="2" fill="#434343" opacity="0.1"/>
                                <rect x="0" y="45" width="45" height="45" rx="4" fill="none" stroke="#434343" strokeWidth="3" opacity="0.15"/>
                                <rect x="7" y="52" width="31" height="31" rx="2" fill="#434343" opacity="0.1"/>
                                <rect x="52" y="0" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="52" y="13" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="52" y="26" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="52" y="39" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="69" y="52" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="82" y="52" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="95" y="52" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="108" y="52" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="69" y="65" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="82" y="78" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="95" y="65" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="108" y="78" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                                <rect x="69" y="78" width="9" height="9" rx="1" fill="#434343" opacity="0.1"/>
                            </svg>
                        </div>

                        {/* Top badges */}
                        <div className="pp-pkg-badges">
                            {(product?.isPopuler) && (
                                <span className="pp-badge pp-badge-populer">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" fill="#096dd9"/>
                                        <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" fill="#096dd9"/>
                                    </svg>
                                    {t('common.popular')}
                                </span>
                            )}
                            {(originalDisplayPrice || promoData?.success) && (
                                <span className="pp-badge pp-badge-promo">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#cf1322"/>
                                    </svg>
                                    {t('common.promo')}
                                </span>
                            )}
                        </div>

                        {/* Main content: left (name + chips) | right (price) */}
                        <div className="pp-pkg-body">
                            <div className="pp-pkg-left">
                                <p className="pp-pkg-title">{cardTitle}</p>
                                <div className="pp-pkg-chips">
                                    {product?.validity && (
                                        <span className="pp-pkg-chip">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" stroke="#595959" strokeWidth="2"/>
                                                <polyline points="12,6 12,12 16,14" stroke="#595959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {t('common.days', { n: product.validity })}
                                        </span>
                                    )}
                                    {/* NOTE: balance is shown in the card title (cardTitle), so it is not
                                        repeated as a chip. Figma's 2nd chip ("Valid 6 bulan") needs an
                                        eSIM validity-period field the product API does not yet return. */}
                                </div>
                            </div>
                            <div className="pp-pkg-right">
                                <p className="pp-pkg-price">{fmt(effectivePrice)}</p>
                                {originalDisplayPrice && (
                                    <div className="pp-pkg-disc-row">
                                        {discountPct > 0 && (
                                            <span className="pp-pkg-disc-badge">{discountPct}%</span>
                                        )}
                                        <span className="pp-pkg-orig">{fmt(originalDisplayPrice)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Email notification bar ── */}
                    {isAuthenticated && (
                        <div className="pp-email-bar">
                            <span className="pp-email-text">
                                {topup
                                    ? t('payment.emailTopup', { iccid })
                                    : t('payment.emailNew', { email: user?.email })
                                }
                            </span>
                        </div>
                    )}
                    </div>{/* end pp-pkg-group */}

                    {/* ── Metode Pembayaran ── */}
                    {isAuthenticated && (
                        <div className="pp-pay-section">
                            <div className="pp-sec-hdr">
                                <div className="pp-section-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <rect x="2" y="5" width="20" height="14" rx="3" stroke="#237804" strokeWidth="2"/>
                                        <path d="M2 10h20" stroke="#237804" strokeWidth="2"/>
                                        <rect x="6" y="14" width="4" height="2" rx="0.5" fill="#237804"/>
                                    </svg>
                                </div>
                                <h2 className="pp-section-title">{t('payment.methodsTitle')}</h2>
                            </div>
                            <div className="pp-methods">
                                {PAYMENT_METHODS.map((method) => (
                                    <button
                                        key={method.id}
                                        className={`pp-method${selectedMethod === method.id ? ' active' : ''}`}
                                        onClick={() => setSelectedMethod(method.id)}
                                    >
                                        <div className={`pp-method-icon-wrap${selectedMethod === method.id ? ' active' : ''}`}>
                                            <img src={method.icon} alt={method.name} className="pp-method-icon" />
                                        </div>
                                        <div className="pp-method-info">
                                            <span className="pp-method-name">{method.name}</span>
                                            <span className="pp-method-sub">{t(`payment.methods.${method.id}`)}</span>
                                        </div>
                                        <div className={`pp-radio${selectedMethod === method.id ? ' checked' : ''}`} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Promo & Voucher ── */}
                    {isAuthenticated && (
                        <div className="pp-promo-section">
                            <div className="pp-sec-hdr">
                                <div className="pp-section-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="#237804" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <line x1="7" y1="7" x2="7.01" y2="7" stroke="#237804" strokeWidth="2.5" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <h2 className="pp-section-title">{t('payment.promoTitle')}</h2>
                            </div>

                            <div className="pp-promo-card">
                                <form className="pp-promo-form" onSubmit={handleApplyPromo}>
                                    <div className="pp-promo-row">
                                        <div className="pp-promo-input-wrap">
                                            <input
                                                type="text"
                                                className="pp-promo-input"
                                                placeholder={t('payment.promoPlaceholder')}
                                                value={promoCode}
                                                onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); setPromoData(null); }}
                                                disabled={promoLoading}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="pp-promo-btn"
                                            disabled={promoLoading || !promoCode.trim()}
                                        >
                                            {promoLoading ? t('payment.promoChecking') : t('payment.promoCheck')}
                                        </button>
                                    </div>
                                    {promoError && <p className="pp-promo-msg error">{promoError}</p>}
                                    {promoData?.success && (
                                        <p className="pp-promo-msg success">
                                            <RichText text={t('payment.promoApplied', { code: promoData.promoCode })} />
                                        </p>
                                    )}
                                </form>

                            </div>
                        </div>
                    )}

                    {/* ── Ada Kendala? ── */}
                    <div className="pp-help-card">
                        <div className="pp-help-content">
                            <div className="pp-help-texts">
                                <p className="pp-help-title">{t('help.title')}</p>
                                <p className="pp-help-desc">{t('help.desc')}</p>
                            </div>
                            <a
                                className="pp-help-chat"
                                href={WA_CS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('help.cta')}
                            </a>
                        </div>
                        <img
                            className="pp-help-illustration"
                            src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>

                    <p className="pp-consent">
                        {t('payment.consent')}
                    </p>
                </div>
            </div>

            {/* ── Bottom pay bar ── */}
            {isAuthenticated && (
                <div className="pp-bottom-bar">
                    <div className="pp-bottom-left">
                        <span className="pp-bottom-label">{t('payment.total')}</span>
                        <div className="pp-bottom-price-row">
                            <span className="pp-bottom-price">{fmt(effectivePrice)}</span>
                            {originalDisplayPrice && Number(originalDisplayPrice) !== Number(effectivePrice) && (
                                <span className="pp-bottom-orig">{fmt(originalDisplayPrice)}</span>
                            )}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="18,15 12,9 6,15" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </div>
                    <button
                        className="pp-pay-btn"
                        onClick={handlePay}
                        disabled={!selectedMethod || orderLoading}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="9,12 11,14 15,10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {orderLoading ? t('payment.processing') : t('payment.payNow')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentPage;
